import { CommerceState } from "../../types";

type CommerceReducer<T> = (
  state: CommerceState,
  action: { payload: T }
) => void;

const generateInternalCartLineItemId = () =>
  Math.random().toString(36).slice(2, 9);

const getDate = (dateString: string | null): Date | null => {
  if (dateString === null) {
    return null;
  }

  const datePartial = dateString.split("/");
  const nextDay = Number(datePartial[1]);
  const nextMonth = Number(datePartial[0]);
  const nextYear = Number(datePartial[2]);

  if (isNaN(nextDay) || isNaN(nextMonth) || isNaN(nextYear)) {
    return null;
  }

  const date = new Date();
  date.setDate(nextDay);
  date.setFullYear(nextYear);
  date.setMonth(nextMonth - 1);
  return date;
};

export const addShippingAddress: CommerceReducer<{ addressId: number }> = (
  state,
  { payload }
) => {
  const { cartData, orderableProducts, userData } = state;
  const addressSelection = userData.addresses.find(
    (address) => address.id === payload.addressId
  );

  if (!addressSelection) {
    return;
  }

  // Find the healthcare license that matches the state and has the furthest
  // expiration date to be the default
  const healthcareLicenses = userData.licenses
    ?.filter((license) => {
      if (license.state !== addressSelection.state) {
        return false;
      }

      const date = getDate(license.expiration);

      return date !== null && date >= new Date(Date.now());
    })
    ?.sort((prev, next) => {
      const datePrev = getDate(prev.expiration);
      const dateNext = getDate(next.expiration);
      return (dateNext?.getTime() || 0) - (datePrev?.getTime() || 0);
    });
  const healthcareLicense = healthcareLicenses[0] ?? null;

  const newCartLineItems = orderableProducts?.map((product) => {
    const internalCartLineItemId = generateInternalCartLineItemId();

    const cartItem: CommerceState["cartData"]["cartLineItems"][0] = {
      cartId: cartData.cartId,
      healthcareLicense: healthcareLicense
        ? {
            expirationDate: healthcareLicense.expiration || undefined,
            licenseNumber: healthcareLicense.number,
            licenseType: healthcareLicense.licenseType,
            name: `${healthcareLicense.firstName} ${healthcareLicense.lastName}`,
            state: healthcareLicense.state
          }
        : {},
      internalCartLineItemId: internalCartLineItemId,
      productId: product?.materialMasterId,
      priceSnapshotId: "",
      purchaseOrderNumber: "",
      quantity: 0,
      shipToAddress: { addressId: String(addressSelection?.id) },
      shipToOrganizationId: String(addressSelection?.organizationId)
    };

    return cartItem;
  });

  state.cartData.cartLineItems = cartData?.cartLineItems
    ? [...cartData?.cartLineItems, ...newCartLineItems]
    : [...newCartLineItems];
};
